import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  Clock,
  CreditCard,
  Database,
  Mail,
  MessageSquare,
  ShieldCheck,
  Table2,
  Users,
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  FEATURE_CHAT_MESSAGES_MONTHLY,
  FEATURE_DATASETS_INDEXED_TOTAL,
  FEATURE_STORAGE_BYTES_TOTAL,
  FEATURE_TRIAL_ENABLED,
} from '../constants/capabilities';
import { useAuth } from '../context/AuthContext';
import { usePostHog } from '../providers/PostHogProvider';
import ContactRequestModal from '../components/account/TrialExtensionModal';

const formatNumber = value => new Intl.NumberFormat().format(Number(value || 0));

const formatBytes = bytes => {
  const n = Number(bytes || 0);
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
};

const usageDisplay = (used, limit, formatValue = formatNumber) => {
  const hasLimit = limit != null && limit !== Infinity && Number(limit) > 0;
  return hasLimit
    ? `${formatValue(used)} / ${formatValue(limit)}`
    : `${formatValue(used)} used`;
};

const UsageCard = ({ icon: Icon, label, used, limit, formatValue = formatNumber, iconClassName }) => {
  const hasLimit = limit != null && limit !== Infinity && Number(limit) > 0;
  const pct = hasLimit ? Math.min(100, Math.max(0, (Number(used) / Number(limit)) * 100)) : null;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white px-3 py-2.5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-center gap-2.5">
        <Icon
          size={15}
          className={cn('shrink-0', iconClassName)}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
              {label}
            </p>
            <p className="text-xs font-bold tabular-nums text-gray-700 dark:text-gray-200">
              {usageDisplay(used, limit, formatValue)}
            </p>
          </div>
          {pct != null && (
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-gray-200/70 dark:bg-gray-800">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500'
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PlanFeatureList = ({ features }) => (
  <ul className="mt-4 space-y-3">
    {features.map(({ headline, detail }) => (
      <li key={headline} className="flex gap-2.5">
        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-emerald-500" />
        <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-400">
          <span className="font-bold text-gray-900 dark:text-white">{headline}. </span>
          {detail}
        </p>
      </li>
    ))}
  </ul>
);

const PlanCard = ({ title, badge, subtitle, features, highlighted = false, footer }) => (
  <section
    className={cn(
      'flex h-full flex-col rounded-3xl border bg-white p-5 shadow-sm dark:bg-gray-900',
      highlighted
        ? 'border-emerald-300 dark:border-emerald-500/40'
        : 'border-gray-100 dark:border-gray-800'
    )}
  >
    {badge ? (
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-xl font-black uppercase tracking-tight text-gray-900 dark:text-white">{title}</h3>
        <span className="inline-flex rounded-lg bg-emerald-500 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-white">
          {badge}
        </span>
      </div>
    ) : (
      <h3 className="text-xl font-black uppercase tracking-tight text-gray-900 dark:text-white">{title}</h3>
    )}
    <div className="mt-2 flex-1">
      {subtitle && (
        <p className="text-xs font-semibold leading-relaxed text-gray-500 dark:text-gray-400">{subtitle}</p>
      )}
      {features && <PlanFeatureList features={features} />}
    </div>
    {footer}
  </section>
);

const ComingSoonBadge = () => (
  <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
    Coming soon
  </span>
);

const CtaCard = ({ icon: Icon, title, description, disabled = false, href, onClick }) => {
  const content = (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-black text-gray-950 dark:text-white">{title}</h3>
          {disabled && <ComingSoonBadge />}
        </div>
        <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      {!disabled && <ArrowRight size={17} className="shrink-0 text-emerald-500" />}
    </div>
  );

  if (disabled) {
    return (
      <div className="rounded-3xl border border-gray-100 bg-white p-4 opacity-75 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        {content}
      </div>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group w-full rounded-3xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-emerald-500/30"
      >
        {content}
      </button>
    );
  }

  return (
    <a
      href={href}
      className="group rounded-3xl border border-gray-100 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-emerald-500/30"
    >
      {content}
    </a>
  );
};

const proFeatures = [
  {
    headline: 'Skip manual exports',
    detail: 'Connect BigQuery, Postgres, and other live databases.',
  },
  {
    headline: 'Stay current',
    detail: 'Every new feature included as we ship it.',
  },
  {
    headline: 'Help when you need it',
    detail: 'Higher limits and priority support.',
  },
];

const pilotOrgFeatures = [
  {
    headline: 'Synced team',
    detail: 'Shared workspaces on one data context.',
  },
  {
    headline: 'Right access per team',
    detail: 'Roles and permissions across your org.',
  },
  {
    headline: 'Stop repeating yourself',
    detail: 'Manteo knows your business context, no more copy-pasting.',
  },
];

const customFeatures = [
  {
    headline: 'Built around your tools',
    detail: 'Integrations and limits matched to your stack.',
  },
  {
    headline: 'Your rules, your environment',
    detail: 'Security, compliance, and deployment your way.',
  },
  {
    headline: 'We onboard with you',
    detail: 'Dedicated setup from our team.',
  },
];

const ACCOUNT_TABS = [
  { id: 'plans', label: 'Plans', icon: CreditCard },
  { id: 'contact', label: 'Contact us', icon: Mail },
  { id: 'privacy', label: 'Privacy', icon: ShieldCheck },
];

const AccountPage = () => {
  const { user, capabilities } = useAuth();
  const { consent, optOut, optIn } = usePostHog();
  const [activeTab, setActiveTab] = useState('plans');
  const [contactRequestType, setContactRequestType] = useState(null);
  const isTrialPlan = !!capabilities?.features?.[FEATURE_TRIAL_ENABLED];


  const handleToggleAnalytics = () => {
    if (consent === 'true') {
      optOut();
    } else {
      optIn();
    }
  };

  const openContactForm = (requestType) => setContactRequestType(requestType);
  const closeContactForm = () => setContactRequestType(null);

  const ctas = [
    {
      icon: Users,
      title: 'Meet founders',
      description: 'Book a working session to shape the pilot around your data and goals.',
      disabled: true,
    },
    ...(isTrialPlan
      ? [
          {
            icon: Clock,
            title: 'Request trial extension',
            description: 'Get more time to test real workflows before choosing a plan.',
            action: 'trial_extension',
          },
        ]
      : []),
    {
      icon: Mail,
      title: 'Email us',
      description: 'Ask questions, share feedback, or talk through the right plan.',
      href: 'mailto:hello@manteo.ai',
    },
  ];

  useEffect(() => {
    document.title = 'manteo.ai | Account';
  }, []);

  const trialEndsAt = capabilities?.billing_period_end
    ? new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(new Date(capabilities.billing_period_end))
    : null;

  const usage = capabilities?.usage || {};
  const limits = capabilities?.limits || {};
  const planName = capabilities?.plan || 'Pilot';
  const usageCards = [
    {
      icon: MessageSquare,
      label: 'Messages',
      iconClassName: 'text-emerald-600 dark:text-emerald-400',
      used: usage[FEATURE_CHAT_MESSAGES_MONTHLY] || 0,
      limit: limits[FEATURE_CHAT_MESSAGES_MONTHLY],
    },
    {
      icon: Database,
      label: 'Storage',
      iconClassName: 'text-blue-500 dark:text-blue-400',
      used: usage[FEATURE_STORAGE_BYTES_TOTAL] || 0,
      limit: limits[FEATURE_STORAGE_BYTES_TOTAL],
      formatValue: formatBytes,
    },
    {
      icon: Table2,
      label: 'Indexed datasets',
      iconClassName: 'text-violet-600 dark:text-violet-400',
      used: usage[FEATURE_DATASETS_INDEXED_TOTAL] || 0,
      limit: limits[FEATURE_DATASETS_INDEXED_TOTAL],
    },
  ];

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50/50 px-8 py-8 transition-colors duration-300 dark:bg-gray-950">
      <div className="mx-auto max-w-7xl space-y-5">
        <header className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <h1 className="text-3xl font-black uppercase tracking-tight text-gray-900 dark:text-white">Account</h1>
          </div>
        </header>

        <div
          role="tablist"
          aria-label="Account sections"
          className="inline-flex w-fit flex-wrap items-center gap-1 rounded-[22px] border border-gray-200/70 bg-white/70 p-1.5 shadow-inner shadow-gray-200/60 backdrop-blur dark:border-gray-800 dark:bg-gray-900/60 dark:shadow-black/20"
        >
          {ACCOUNT_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'group inline-flex items-center gap-2 rounded-2xl px-4 py-2 font-black text-[10px] uppercase tracking-widest transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40',
                activeTab === tab.id
                  ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-300 dark:bg-gray-800 dark:text-emerald-300 dark:ring-emerald-700/70'
                  : 'text-gray-500 hover:bg-gray-100/80 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/80 dark:hover:text-white'
              )}
            >
              <tab.icon size={14} className="shrink-0" strokeWidth={2.5} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          {activeTab === 'plans' && (
            <div className="space-y-5">
              <section className="space-y-2">
                <div>
                  <h2 className="text-base font-black text-gray-950 dark:text-white">Usage</h2>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {user?.email || 'Signed in user'}
                    <span className="text-gray-300 dark:text-gray-700"> · </span>
                    {planName} plan
                    {isTrialPlan && trialEndsAt && (
                      <>
                        <span className="text-gray-300 dark:text-gray-700"> · </span>
                        Trial ends {trialEndsAt}
                      </>
                    )}
                  </p>
                </div>

                <div className="grid gap-2 md:grid-cols-3">
                  {usageCards.map(card => (
                    <UsageCard key={card.label} {...card} />
                  ))}
                </div>
              </section>

              <section className="space-y-3">
                <h2 className="text-base font-black text-gray-950 dark:text-white">Plans</h2>

                <div className="grid gap-3 lg:grid-cols-3">
                  <PlanCard
                    title="Premium"
                    subtitle="Your databases, connected. Ask anything and get answers you can trust."
                    features={proFeatures}
                    footer={
                      <button
                        type="button"
                        onClick={() => openContactForm('pro')}
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-emerald-700"
                      >
                        Get Premium Plan
                        <ArrowRight size={14} />
                      </button>
                    }
                  />

                  <PlanCard
                    title="Pilot for Organizations"
                    subtitle="Your whole team on the same data. Stop re-explaining and start deciding together."
                    features={pilotOrgFeatures}
                    footer={
                      <button
                        type="button"
                        onClick={() => openContactForm('team')}
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-5 py-2.5 text-xs font-black uppercase tracking-widest text-gray-900 transition-colors hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:hover:border-gray-600"
                      >
                        Pilot Request
                        <ArrowRight size={14} />
                      </button>
                    }
                  />

                  <PlanCard
                    title="Custom"
                    subtitle="Tell us what you need and we'll tailor Manteo to your stack, security, and scale."
                    features={customFeatures}
                    footer={
                      <button
                        type="button"
                        onClick={() => openContactForm('custom')}
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-5 py-2.5 text-xs font-black uppercase tracking-widest text-gray-900 transition-colors hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:hover:border-gray-600"
                      >
                        Custom Request
                        <ArrowRight size={14} />
                      </button>
                    }
                  />
                </div>
              </section>
            </div>
          )}

          {activeTab === 'contact' && (
            <section className="space-y-3">
              <div>
                <h2 className="text-base font-black text-gray-950 dark:text-white">Make the most of Manteo</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Get help shaping the trial, extending evaluation, or choosing the right plan.</p>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                {ctas.map(cta => (
                  <CtaCard
                    key={cta.title}
                    {...cta}
                    onClick={
                      cta.action === 'trial_extension'
                        ? () => openContactForm('trial_extension')
                        : undefined
                    }
                  />
                ))}
              </div>
            </section>
          )}

          {activeTab === 'privacy' && (
            <section className="space-y-3">
              <div>
                <h2 className="text-base font-black text-gray-950 dark:text-white">Privacy & Preferences</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Manage how Manteo handles your data and analytics.</p>
              </div>

              <div className="rounded-[30px] border border-gray-100 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                      <ShieldCheck size={20} />
                    </div>
                    <div className="flex flex-1 items-center justify-between gap-8">
                      <div className="space-y-1">
                        <p className="text-xs font-bold uppercase tracking-tight text-gray-900 dark:text-white">Usage Analytics</p>
                        <p className="text-[10px] leading-relaxed text-gray-500 dark:text-gray-400">
                          Help us improve Manteo by sharing anonymous usage data. We <span className="font-bold text-emerald-600 underline decoration-emerald-500/30 underline-offset-2 dark:text-emerald-400">never sell or share your information</span>.
                        </p>
                        <div className="max-w-sm rounded-lg border border-amber-100/50 bg-amber-50/50 p-1.5 dark:border-amber-800/30 dark:bg-amber-900/10">
                          <p className="flex items-center gap-1.5 text-[9px] font-bold text-amber-800 dark:text-amber-300">
                            <ShieldCheck className="h-2.5 w-2.5" />
                            Tip: Disabling Ad-Blockers helps us track bugs better!
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleToggleAnalytics}
                        type="button"
                        className={cn(
                          'relative z-10 inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                          consent === 'true' ? 'bg-emerald-600' : 'bg-gray-200 dark:bg-gray-800'
                        )}
                      >
                        <span
                          className={cn(
                            'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                            consent === 'true' ? 'translate-x-4' : 'translate-x-0'
                          )}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        <ContactRequestModal
          isOpen={!!contactRequestType}
          requestType={contactRequestType}
          onClose={closeContactForm}
          userEmail={user?.email}
          userFullName={user?.full_name}
          planName={capabilities?.plan}
          trialEndsAt={trialEndsAt}
        />
      </div>
    </div>
  );
};

export default AccountPage;
